import axios from "axios";
import { Socket } from "socket.io-client";
import { sessionModel } from "entities/session";
import { sircapApi } from "shared/api/sircap";

/** Не чаще одной попытки обновления за этот интервал. */
const MIN_RETRY_INTERVAL_MS = 5000;
/** Сколько раз подряд пытаемся восстановить сокет, прежде чем сдаться. */
const MAX_AUTH_RECOVERY_ATTEMPTS = 5;

let lastAttemptAt = 0;
let inFlight: Promise<boolean> | null = null;

/**
 * Обновляет access-токен ради переподключения сокета.
 *
 * Запрос идёт напрямую через axios, а не через sircapApi, намеренно. У клиента
 * заголовки securityWorker мержатся ПОСЛЕ пер-запросных, поэтому подставить
 * refresh-токен точечно нельзя, а глобальная подмена securityData сломала бы
 * любой параллельный запрос: он ушёл бы с refresh-токеном, получил Forbidden и
 * общий обработчик выкинул бы пользователя на логин.
 *
 * Если обновление не удалось, сокет остаётся лежать и мы ничего не ломаем:
 * первый же обычный HTTP-запрос упрётся в тот же отказ и штатный обработчик
 * разлогинит пользователя как обычно. Дублировать логику выхода здесь незачем.
 */
export function refreshAccessTokenForSocket(): Promise<boolean> {
  if (inFlight) return inFlight;

  const now = Date.now();
  if (now - lastAttemptAt < MIN_RETRY_INTERVAL_MS) return Promise.resolve(false);
  lastAttemptAt = now;

  inFlight = requestFreshAccessToken().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

async function requestFreshAccessToken(): Promise<boolean> {
  const { refreshToken } = sessionModel.sessionStore.getState();
  if (!refreshToken || !sircapApi.baseUrl) return false;

  try {
    const response = await axios.get(`${sircapApi.baseUrl}/auth/token`, {
      headers: { Authorization: `Bearer ${refreshToken}` },
      timeout: 15000,
    });

    const data = response?.data?.data;
    if (!data?.accessToken) return false;

    sessionModel.sessionStore.setState({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken || refreshToken,
    });
    sircapApi.setSecurityData(data.accessToken);

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Поднимает сокет после отказа авторизации.
 *
 * socket.io различает два вида падения: при транспортной ошибке `socket.active`
 * остаётся true и клиент переподключается сам, а при отказе middleware — false,
 * и тогда он НЕ делает больше ни одной попытки. Второй случай наступает ровно
 * тогда, когда протух access-токен, и без этой обвязки сокет умирает молча и
 * навсегда — до перезагрузки страницы.
 */
export function attachAuthRecovery(socket: Socket): void {
  let failures = 0;

  socket.on("connect", () => {
    failures = 0;
  });

  socket.on("connect_error", async () => {
    // Транспортная ошибка — socket.io повторит сам, вмешиваться не надо.
    if (socket.active) return;
    if (failures >= MAX_AUTH_RECOVERY_ATTEMPTS) return;

    failures++;
    const refreshed = await refreshAccessTokenForSocket();
    if (refreshed) socket.connect();
  });
}

/** Токен для рукопожатия. Функцией — чтобы пересчитывался на каждой попытке. */
export function socketAuth(cb: (data: { token: string }) => void): void {
  cb({ token: sessionModel.sessionStore.getState().accessToken });
}
